import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';
import { Folder } from '../types/shared';
import { savedAdsApi, foldersApi } from '../services/api';

interface FolderState {
  folders: Folder[];
  foldersTree: Folder[];
  selectedFolder: Folder | null;
  isLoading: boolean;
  error: string | null;
}

type FolderAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_FOLDERS'; payload: Folder[] }
  | { type: 'SET_FOLDERS_TREE'; payload: Folder[] }
  | { type: 'SELECT_FOLDER'; payload: Folder | null }
  | { type: 'ADD_FOLDER'; payload: Folder }
  | { type: 'UPDATE_FOLDER'; payload: Folder }
  | { type: 'DELETE_FOLDER'; payload: string }
  | { type: 'MOVE_FOLDER'; payload: { folderId: string; newParentId: string | null } };

interface FolderContextType extends FolderState {
  // Actions
  createFolder: (name: string, parentId?: string | null, color?: string, icon?: string) => Promise<Folder>;
  updateFolder: (id: string, updates: { name?: string; color?: string; icon?: string }) => Promise<Folder>;
  deleteFolder: (id: string, moveAdsToFolderId?: string) => Promise<void>;
  moveFolder: (folderId: string, newParentId: string | null) => Promise<Folder>;
  selectFolder: (folder: Folder | null) => void;
  refreshFolders: () => Promise<void>;
  getFolderPath: (folderId: string) => string;
  getFolderById: (folderId: string) => Folder | null;
}

const FolderContext = createContext<FolderContextType | undefined>(undefined);

const folderReducer = (state: FolderState, action: FolderAction): FolderState => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    
    case 'SET_ERROR':
      return { ...state, error: action.payload };
    
    case 'SET_FOLDERS':
      return { ...state, folders: action.payload };
    
    case 'SET_FOLDERS_TREE':
      return { ...state, foldersTree: action.payload };
    
    case 'SELECT_FOLDER':
      return { ...state, selectedFolder: action.payload };
    
    case 'ADD_FOLDER':
      return {
        ...state,
        folders: [...state.folders, action.payload],
        foldersTree: buildTree([...state.folders, action.payload])
      };
    
    case 'UPDATE_FOLDER':
      const updatedFolders = state.folders.map(folder =>
        folder._id === action.payload._id ? action.payload : folder
      );
      return {
        ...state,
        folders: updatedFolders,
        foldersTree: buildTree(updatedFolders)
      };
    
    case 'DELETE_FOLDER':
      const filteredFolders = state.folders.filter(folder => folder._id !== action.payload);
      return {
        ...state,
        folders: filteredFolders,
        foldersTree: buildTree(filteredFolders),
        selectedFolder: state.selectedFolder?._id === action.payload ? null : state.selectedFolder
      };
    
    case 'MOVE_FOLDER':
      const movedFolders = state.folders.map(folder =>
        folder._id === action.payload.folderId
          ? { ...folder, parentId: action.payload.newParentId }
          : folder
      );
      return {
        ...state,
        folders: movedFolders,
        foldersTree: buildTree(movedFolders)
      };
    
    default:
      return state;
  }
};

// Helper function to build tree structure
const buildTree = (folders: Folder[]): Folder[] => {
  const folderMap = new Map<string, Folder & { children: Folder[] }>();
  const rootFolders: (Folder & { children: Folder[] })[] = [];

  // First pass: create map with children array
  folders.forEach(folder => {
    folderMap.set(folder._id, { ...folder, children: [] });
  });

  // Second pass: build tree
  folders.forEach(folder => {
    const folderNode = folderMap.get(folder._id)!;
    if (folder.parentId) {
      const parent = folderMap.get(folder.parentId);
      if (parent) {
        parent.children.push(folderNode);
      }
    } else {
      rootFolders.push(folderNode);
    }
  });

  return rootFolders;
};

export const FolderProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(folderReducer, {
    folders: [],
    foldersTree: [],
    selectedFolder: null,
    isLoading: false,
    error: null
  });

  // Load folders on mount and when window gains focus
  useEffect(() => {
    refreshFolders();

    // Refetch folders when tab/window gains focus
    const handleFocus = () => {
      refreshFolders();
    };

    window.addEventListener('focus', handleFocus);

    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  const createFolder = async (
    name: string,
    parentId?: string | null,
    color: string = '#3B82F6',
    icon: string = 'folder'
  ): Promise<Folder> => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'SET_ERROR', payload: null });

      const data = await foldersApi.createFolder({ name, parentId, color, icon });
      dispatch({ type: 'ADD_FOLDER', payload: data.folder });
      
      return data.folder;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Error al crear carpeta';
      dispatch({ type: 'SET_ERROR', payload: errorMessage });
      throw error;
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const updateFolder = async (
    id: string,
    updates: { name?: string; color?: string; icon?: string }
  ): Promise<Folder> => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'SET_ERROR', payload: null });

      const data = await foldersApi.updateFolder(id, updates);
      dispatch({ type: 'UPDATE_FOLDER', payload: data.folder });
      
      return data.folder;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Error al actualizar carpeta';
      dispatch({ type: 'SET_ERROR', payload: errorMessage });
      throw error;
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const deleteFolder = async (id: string, moveAdsToFolderId?: string): Promise<void> => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'SET_ERROR', payload: null });

      await foldersApi.deleteFolder(id, moveAdsToFolderId);

      dispatch({ type: 'DELETE_FOLDER', payload: id });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Error al eliminar carpeta';
      dispatch({ type: 'SET_ERROR', payload: errorMessage });
      throw error;
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const moveFolder = async (folderId: string, newParentId: string | null): Promise<Folder> => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'SET_ERROR', payload: null });

      const data = await foldersApi.moveFolder(folderId, { newParentId });
      dispatch({ type: 'MOVE_FOLDER', payload: { folderId, newParentId } });
      
      return data.folder;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Error al mover carpeta';
      dispatch({ type: 'SET_ERROR', payload: errorMessage });
      throw error;
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const selectFolder = (folder: Folder | null) => {
    dispatch({ type: 'SELECT_FOLDER', payload: folder });
  };

  const refreshFolders = async (): Promise<void> => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'SET_ERROR', payload: null });

      const data = await foldersApi.getFoldersTree();
      
      // Flatten tree to get all folders
      const allFolders: Folder[] = [];
      const flattenTree = (tree: Folder[]) => {
        tree.forEach(folder => {
          allFolders.push(folder);
          if (folder.children) {
            flattenTree(folder.children);
          }
        });
      };
      
      flattenTree(data.tree);
      
      dispatch({ type: 'SET_FOLDERS', payload: allFolders });
      dispatch({ type: 'SET_FOLDERS_TREE', payload: data.tree });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Error al cargar carpetas';
      dispatch({ type: 'SET_ERROR', payload: errorMessage });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const getFolderPath = (folderId: string): string => {
    const folder = getFolderById(folderId);
    return folder ? folder.path : '';
  };

  const getFolderById = (folderId: string): Folder | null => {
    return state.folders.find(folder => folder._id === folderId) || null;
  };

  const contextValue: FolderContextType = {
    ...state,
    createFolder,
    updateFolder,
    deleteFolder,
    moveFolder,
    selectFolder,
    refreshFolders,
    getFolderPath,
    getFolderById
  };

  return (
    <FolderContext.Provider value={contextValue}>
      {children}
    </FolderContext.Provider>
  );
};

export const useFolders = (): FolderContextType => {
  const context = useContext(FolderContext);
  if (context === undefined) {
    throw new Error('useFolders must be used within a FolderProvider');
  }
  return context;
};

